//core
import React, {useCallback, useEffect, useState} from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box, Button,
  Card,
  Typography,
  useMediaQuery
} from "@mui/material";

import AddBoxIcon from "@mui/icons-material/AddBox";
import IndeterminateIcon from "@mui/icons-material/IndeterminateCheckBox";


//styles
import { useStyles } from "./styles";

//context
import { usePayoutsContext } from "./PayoutsContext";
import { PayoutsProvider } from "./PayoutsProvider";
import { OrderType } from "../../../../../context/GlobalContext";

//selectors
import {
  selectCreatePayoutData, selectCreatePayoutError,
  selectGetPayoutsListData, selectRemovePayoutData, selectUpdatePayoutData, selectUpdatePayoutError
} from "../../../../../store/admin/flatFee/selectors";

//hooks
import useQuery from "../../../../../customHooks/useQuery";
import { useFlatFee } from "../../../../../store/admin/flatFee/useFlatFee";

//components
import TableData, {itemColumnsData, itemListData} from "../../../../common/TableData";
import EmptyTable from "../../../../common/TableData/components/EmptyTable";
import CreateUpdatePayout from "./components/CreateUpdatePayout";
import {usePermissions} from "../../../../../store/common/configuration/usePermissions";
import TableActions from "./components/TableActions";
import useDidMountEffect from "../../../../../customHooks/useDidMountEffect";
import MobileCard from "./components/MobileCard";
import MuiModal from "../../../../UI/MuiModal";


interface IDataType {
  columns: itemColumnsData[];
  list: itemListData[];
  start_row_num: number;
  end_row_num: number;
  total_entries: number;
  total_pages: number;
}


interface IPayoutsProps {
  paymentData: { [key: string]: any; };
  affiliateId?: any;
  hideAffId?: boolean;
  flatFeeId?: any;
}

const Payouts: React.FC<IPayoutsProps> = ({
  paymentData,
  affiliateId = null,
  hideAffId = false,
  flatFeeId,
}): JSX.Element => {
  const styles = useStyles();
  const { t } = useTranslation();
  let query = useQuery();
  const { reportId } = useParams();
  const mobile = useMediaQuery('(max-width:767px)');
  const {hasPermissions} = usePermissions();

  const { getPayouts, clearPayoutsList, clearCreatePayout, clearUpdatePayout, clearRemovePayout, clearPaymentState } = useFlatFee();

  const localContext = usePayoutsContext();

  const payoutsData = useSelector(selectGetPayoutsListData);
  const createdNewPayout = useSelector(selectCreatePayoutData);
  const updatePayout = useSelector(selectUpdatePayoutData);
  const removeData = useSelector(selectRemovePayoutData);
  const createError = useSelector(selectCreatePayoutError);
  const updateError = useSelector(selectUpdatePayoutError);

  const isPermissionShowCreate = hasPermissions(["api3.admin.flatfeecontroller.payment_create"]);
  const isPermissionShowEdit = hasPermissions(["api3.admin.flatfeecontroller.payment_edit"]);
  const isPermissionShowDelete = hasPermissions(["api3.admin.flatfeecontroller.payment_delete"]);

  const [data, setData] = useState<IDataType | null>(null);
  const [order, setOrder] = useState<any>({ order: '', orderBy: '' });
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [actionsCount, setActionsCount] = useState<number>(0);
  const [showFormModal, setShowFormModal] = useState<boolean>(false);
  const [disableCreateBtn, setDisableCreateBtn] = useState<boolean>(true);

  /* action calculation */
  useEffect(() => {
    if (isPermissionShowEdit) {
      setActionsCount((prevState) => {
        return prevState + 1
      });
    }
  }, [isPermissionShowEdit]);
  useEffect(() => {
    if (isPermissionShowDelete) {
      setActionsCount((prevState) => {
        return prevState + 1
      });
    }
  }, [isPermissionShowDelete]);
  /* end action calculation */

  useEffect(() => {
    if (!payoutsData) {
      const payload = getPayloadData();
      getPayouts(payload);
    }
  }, [payoutsData]);

  useEffect(() => {
    if (!!createdNewPayout) {
      const payload = getPayloadData();
      getPayouts(payload);
      localContext.setShowCreateUpdatePayout(!localContext.showCreateUpdatePayout)
      clearCreatePayout();
    }
  }, [createdNewPayout]);

  useEffect(() => {
    if (!!updatePayout) {
      const payload = getPayloadData();
      getPayouts(payload);
      localContext.setShowCreateUpdatePayout(!localContext.showCreateUpdatePayout)
      clearUpdatePayout();
    }
  }, [updatePayout]);

  useEffect(() => {
    if (!!removeData) {
      const payload = getPayloadData();
      getPayouts(payload);
      clearRemovePayout();
    }
  }, [removeData]);

  useEffect(() => {
    if (!!payoutsData) {
      setData({
        ...payoutsData,
        start_row_num: 1,
        end_row_num: payoutsData.list.length,
        total_entries: payoutsData.list.length,
        total_pages: 1,
      })
    }
  }, [payoutsData]);

  useEffect(() => {
    return () => {
      clearPaymentState();
      clearPayoutsList();
      localContext.setDataCreateUpdatePayout(null);
    };
  }, []);

  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setPage(newPage);
  }, [setPage]);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
  }, [setRowsPerPage, setPage]);

  const getPayloadData = () => {
    return {
      flatFeeId: paymentData.id,
    }
  };

  const handleShowNewPayout = () => {
    localContext.setShowCreateUpdatePayout(!localContext.showCreateUpdatePayout)
    localContext.setDataCreateUpdatePayout(null);
    if (!!createError || !!updateError) {
      clearCreatePayout();
      clearUpdatePayout();
    }
    mobile && setShowFormModal(true);
  };

  const handleToggleFormModal = useCallback((state: boolean) => {
    setShowFormModal(state);
  },[setShowFormModal, localContext]);

  useEffect(() => {
    if (!!data) {
      setDisableCreateBtn(data?.list?.length >= 1 || paymentData.status.toLowerCase() === "annulated" || paymentData.status.toLowerCase() === "closed");
    }
  }, [data, paymentData]);

  return (
    <>
      <Box className={styles.block}>
        {!mobile && (
          <Typography className={styles.title} variant="h6">
            {t("admin.reports.detailed_fin_report.payouts.title")}
          </Typography>
        )}

        {!hideAffId && !!affiliateId && (
          <Box className={styles.title_id}>
            <Typography sx={{mb: 2, marginRight: 1}} variant="h6">{t("admin.details_finance_report.adjustments.aff_title")}</Typography>
            <Typography sx={{mb: 2}} variant="h6">{affiliateId}</Typography>
          </Box>
        )}

        {isPermissionShowCreate && (<Button
          className={styles.btn}
          size="medium"
          color="primary"
          variant="text"
          startIcon={localContext.showCreateUpdatePayout && !mobile ? <IndeterminateIcon fontSize="medium"/> : <AddBoxIcon fontSize="medium"/>}
          onClick={handleShowNewPayout}
          disabled={disableCreateBtn}
        >{t("common.buttons.add_payment")}</Button>)}

        {localContext.showCreateUpdatePayout && !mobile && (
          <Box className={styles.new_payout}>
            <CreateUpdatePayout data={paymentData}/>
          </Box>
        )}

        {
          mobile && (
            <MuiModal
              width={505}
              open={showFormModal}
              closeOnOutside={false}
              setToggleModal={handleToggleFormModal}
            >
              <CreateUpdatePayout
                data={paymentData}
                closeModal={handleToggleFormModal}
              />
            </MuiModal>
          )
        }

        {!!data && !data.list.length && <EmptyTable />}

        {!!data && !!data.list.length && (
          <Box className={styles.content}>
            <>
              {mobile ? (
                <MobileCard paymentData={paymentData} data={data} affiliateId={affiliateId} flatFeeId={flatFeeId}/>
              ) : (
                <TableData
                  list={data.list}
                  columns={data.columns}
                  order={order}
                  setOrder={setOrder}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  totalEntries={+data.total_entries}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  actionsCount={!!actionsCount ? actionsCount : 1}
                  {...(paymentData.periodStatus !== 'closed' && !!actionsCount ? {ActionsComponent: TableActions} : {})}
                  translationColumnsPrefix="columns_for_display.table.generate_report."
                  startRowNum={data.start_row_num}
                  endRowNum={data.end_row_num}
                  hidePagination
                  elevation={0}
                />
              )}
            </>
          </Box>
        )}
      </Box>
    </>
  );
};

const PayoutsWithProvider: React.FC<IPayoutsProps> = (props) => (
  <PayoutsProvider paymentData={props.paymentData}>
    <Payouts {...props} />
  </PayoutsProvider>
);

export default PayoutsWithProvider;
